import { useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { io } from "socket.io-client";
import { socket_url } from "config";
import ChatService from "services/chat";
import ProductService from "services/product";
import OrderService from "services/order";
import { formatCurrency } from "utils/number";

const service = new ChatService();
const productService = new ProductService();
const orderService = new OrderService();
const SOCKET_OPTIONS = {
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 8000,
    timeout: 20000,
};
const getAdminSoundStorageKey = () => {
    const token = String(localStorage.getItem("token") || "").trim();
    const suffix = token ? token.slice(-12) : "guest";
    return `chat.sound.admin.${suffix}`;
};

const URL_PATTERN = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

const normalizeURL = (value = "") => {
    const raw = String(value || "").trim();
    if (!raw) {
        return "";
    }
    if (/^https?:\/\//i.test(raw)) {
        return raw;
    }
    return `https://${raw}`;
};

const extractURLs = (text = "") => {
    const matches = String(text || "").match(URL_PATTERN) || [];
    const urls = matches.map((item) => normalizeURL(item)).filter(Boolean);
    return [...new Set(urls)];
};

const firstPositiveNumber = (...values) => {
    for (let index = 0; index < values.length; index++) {
        const n = Number(values[index] || 0);
        if (n > 0) {
            return n;
        }
    }
    return 0;
};

const resolveFileURL = (value) => {
    if (!value) {
        return "";
    }

    if (typeof value === "string") {
        return String(value || "").trim();
    }

    if (typeof value === "object" && !Array.isArray(value)) {
        const picked = value.lg_url || value.md_url || value.url || value.sm_url || "";
        return String(picked || "").trim();
    }

    return "";
};

const mapFilesToImageAttachments = (files = []) => {
    return (files || [])
        .map((file, index) => {
            const url = (
                resolveFileURL(file?.url) ||
                resolveFileURL(file) ||
                ""
            );

            if (!url) {
                return null;
            }

            return {
                type: "image",
                title: String(file?.name || file?.file_name || `Ảnh ${index + 1}`).trim(),
                url,
                meta: file,
            };
        })
        .filter(Boolean);
};

const playNotificationTone = () => {
    try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) {
            return;
        }

        const context = new AudioContextClass();
        const oscillator = context.createOscillator();
        const gainNode = context.createGain();
        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(880, context.currentTime);
        gainNode.gain.setValueAtTime(0.001, context.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.2);
        oscillator.connect(gainNode);
        gainNode.connect(context.destination);
        oscillator.start(context.currentTime);
        oscillator.stop(context.currentTime + 0.22);
        oscillator.onended = () => context.close();
    } catch (error) {
        // ignore sound errors
    }
};

const sortThreads = (items = []) => {
    return [...items].sort((a, b) => new Date(b.update_on || 0) - new Date(a.update_on || 0));
};

const normalizePhone = (value = "") => String(value || "").replace(/\D+/g, "");

const resolveShippingAddress = (order = {}) => {
    if (!order || typeof order !== "object") {
        return "";
    }

    if (typeof order.shipping_address === "string") {
        return String(order.shipping_address || "").trim();
    }

    return String(
        order?.shipping_address?.address ||
        order?.shipping_address?.full_address ||
        order?.customer_address ||
        order?.address ||
        "",
    ).trim();
};

const formatOrderDate = (value) => {
    if (!value) {
        return "";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return new Intl.DateTimeFormat("vi-VN", {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(date);
};

const normalizeSearchText = (value = "") => String(value || "").trim().toLowerCase();

const matchesProductKeyword = (product = {}, keyword = "") => {
    const text = normalizeSearchText(keyword);
    if (!text) {
        return true;
    }

    const candidates = [
        product?.title,
        product?.name,
        product?.sku,
        product?.code,
        product?.product_code,
        product?.barcode,
        product?.id,
    ];

    return candidates.some((item) => normalizeSearchText(item).includes(text));
};

const isSameCustomerOrder = (order = {}, thread = {}) => {
    const threadCustomerID = String(thread?.customer_id || "").trim();
    const orderCustomerID = String(order?.customer_id || order?.customer?.id || "").trim();
    if (threadCustomerID && orderCustomerID) {
        return threadCustomerID === orderCustomerID;
    }

    const threadPhone = normalizePhone(thread?.customer_phone || "");
    const orderPhone = normalizePhone(
        order?.customer_phone ||
        order?.customer?.phone ||
        order?.shipping_address?.phone ||
        order?.phone ||
        "",
    );
    if (threadPhone && orderPhone) {
        return threadPhone === orderPhone;
    }

    const threadName = String(thread?.customer_name || "").trim().toLowerCase();
    const orderName = String(order?.customer_name || order?.customer?.name || "").trim().toLowerCase();
    if (threadName && orderName) {
        return threadName === orderName;
    }

    return false;
};

const matchesKeyword = (thread, keyword = "") => {
    const text = String(keyword || "").trim().toLowerCase();
    if (!text) {
        return true;
    }

    const customerName = String(thread?.customer_name || "").toLowerCase();
    const customerPhone = String(thread?.customer_phone || "").toLowerCase();
    const lastMessage = String(thread?.last_message || "").toLowerCase();
    return customerName.includes(text) || customerPhone.includes(text) || lastMessage.includes(text);
};

const useChat = () => {
    const [loading, setLoading] = useState(true);
    const [messageLoading, setMessageLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [threads, setThreads] = useState([]);
    const [selectedThread, setSelectedThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [linkPreviews, setLinkPreviews] = useState({});
    const [content, setContent] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [attachmentPickerOpen, setAttachmentPickerOpen] = useState(false);
    const [attachmentPickerType, setAttachmentPickerType] = useState("product");
    const [attachmentPickerKeyword, setAttachmentPickerKeyword] = useState("");
    const [attachmentPickerLoading, setAttachmentPickerLoading] = useState(false);
    const [attachmentPickerData, setAttachmentPickerData] = useState([]);
    const [typingText, setTypingText] = useState("");
    const [soundEnabled, setSoundEnabled] = useState(() => {
        const value = localStorage.getItem(getAdminSoundStorageKey());
        return value !== "0";
    });
    const [keyword, setKeyword] = useState("");
    const socketRef = useRef(null);
    const selectedThreadRef = useRef(null);
    const keywordRef = useRef("");
    const previewFetchingRef = useRef({});
    const typingEmitTimeoutRef = useRef(null);
    const typingClearTimeoutRef = useRef(null);

    useEffect(() => {
        loadThreads();

        if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
            Notification.requestPermission().catch(() => null);
        }
    }, []);

    useEffect(() => {
        const allUrls = messages.flatMap((message) => extractURLs(message?.content || ""));
        const uniqueUrls = [...new Set(allUrls)];
        uniqueUrls.forEach(async (url) => {
            if (linkPreviews[url] || previewFetchingRef.current[url]) {
                return;
            }

            previewFetchingRef.current[url] = true;
            try {
                const preview = await service.getLinkPreview({ url });
                if (preview?.url) {
                    setLinkPreviews((prev) => ({ ...prev, [url]: preview }));
                }
            } catch (error) {
                // ignore preview errors
            }
            delete previewFetchingRef.current[url];
        });
    }, [messages]);

    useEffect(() => {
        localStorage.setItem(getAdminSoundStorageKey(), soundEnabled ? "1" : "0");
    }, [soundEnabled]);

    useEffect(() => {
        keywordRef.current = keyword;
    }, [keyword]);

    useEffect(() => {
        selectedThreadRef.current = selectedThread;
    }, [selectedThread]);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) {
            return;
        }

        const socket = io(socket_url, {
            ...SOCKET_OPTIONS,
            auth: { token },
        });

        socketRef.current = socket;

        socket.on("connect", () => {
            const activeThreadID = selectedThreadRef.current?.id;
            if (activeThreadID) {
                socket.emit("chat:join_thread", { thread_id: activeThreadID });
            }
        });

        socket.on("reconnect", () => {
            const activeThreadID = selectedThreadRef.current?.id;
            if (activeThreadID) {
                socket.emit("chat:join_thread", { thread_id: activeThreadID });
            }
        });

        socket.on("connect_error", () => {
            // socket.io will auto reconnect with the configured options above
        });

        socket.on("chat:thread_updated", (thread) => {
            if (!thread) {
                return;
            }

            setThreads((prev) => {
                const hasMatch = prev.some((item) => item.id === thread.id);
                const next = prev.filter((item) => item.id !== thread.id);

                if (matchesKeyword(thread, keywordRef.current)) {
                    next.push(thread);
                } else if (!hasMatch) {
                    return prev;
                }

                return sortThreads(next);
            });

            if (selectedThreadRef.current?.id === thread.id) {
                setSelectedThread(thread);
            }
        });

        socket.on("chat:new_message", (message) => {
            if (!message || selectedThreadRef.current?.id !== message.thread_id) {
                if (message?.sender_type !== "shop_staff") {
                    if (soundEnabled) {
                        playNotificationTone();
                    }
                    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
                        const customerLabel = message?.sender_name || "Khách hàng";
                        const body = String(message?.content || "Tin nhắn mới").slice(0, 120);
                        new Notification(`Tin nhắn mới từ ${customerLabel}`, { body });
                    }
                }
                return;
            }

            setMessages((prev) => {
                if (prev.some((item) => item.id === message.id)) {
                    return prev;
                }
                return [...prev, message];
            });

            if (message.sender_type !== "shop_staff") {
                if (soundEnabled) {
                    playNotificationTone();
                }
                if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
                    const customerLabel = message?.sender_name || "Khách hàng";
                    const body = String(message?.content || "Tin nhắn mới").slice(0, 120);
                    new Notification(`Tin nhắn mới từ ${customerLabel}`, { body });
                }
                markThreadAsRead(message.thread_id);
            }
        });

        socket.on("chat:typing", (payload = {}) => {
            if (payload?.thread_id !== selectedThreadRef.current?.id) {
                return;
            }

            if (payload?.sender_type !== "customer") {
                return;
            }

            const label = payload?.sender_name || selectedThreadRef.current?.customer_name || "Khách hàng";
            if (payload?.is_typing) {
                setTypingText(`${label} đang nhập...`);
                if (typingClearTimeoutRef.current) {
                    clearTimeout(typingClearTimeoutRef.current);
                }
                typingClearTimeoutRef.current = setTimeout(() => {
                    setTypingText("");
                }, 2000);
                return;
            }

            setTypingText("");
        });

        return () => {
            if (typingEmitTimeoutRef.current) {
                clearTimeout(typingEmitTimeoutRef.current);
            }
            if (typingClearTimeoutRef.current) {
                clearTimeout(typingClearTimeoutRef.current);
            }
            socket.disconnect();
            socketRef.current = null;
        };
    }, [soundEnabled]);

    useEffect(() => {
        const socket = socketRef.current;
        if (!socket || !selectedThread?.id) {
            return;
        }

        socket.emit("chat:join_thread", { thread_id: selectedThread.id });

        return () => {
            socket.emit("chat:leave_thread", { thread_id: selectedThread.id });
        };
    }, [selectedThread?.id]);

    const markThreadAsRead = async (threadID) => {
        if (!threadID) {
            return;
        }

        try {
            const updatedThread = await service.markRead({ thread_id: threadID });
            if (!updatedThread) {
                return;
            }

            setThreads((prev) => sortThreads(prev.map((item) => (item.id === updatedThread.id ? updatedThread : item))));
            if (selectedThreadRef.current?.id === updatedThread.id) {
                setSelectedThread(updatedThread);
            }
        } catch (error) {
            // ignore
        }
    };

    const loadThreads = async (searchText = keyword) => {
        setLoading(true);
        try {
            const data = await service.listThreads({ page: 1, page_size: 50, info: searchText });
            const nextThreads = sortThreads(data || []);
            setThreads(nextThreads);
            const nextSelected = (data || []).find((x) => x.id === selectedThread?.id) || data?.[0] || null;
            setSelectedThread(nextSelected || null);
            if (nextSelected?.id) {
                await loadMessages(nextSelected.id);
            } else {
                setMessageLoading(false);
                setMessages([]);
            }
        } catch (error) {
            setMessageLoading(false);
            setThreads([]);
            setMessages([]);
        }
        setLoading(false);
    };

    const loadMessages = async (threadID) => {
        if (!threadID) {
            return;
        }

        setMessageLoading(true);
        try {
            const data = await service.listMessages({ thread_id: threadID, page: 1, page_size: 100 });
            setMessages(data?.messages || []);
            await markThreadAsRead(threadID);
        } catch (error) {
            setMessages([]);
        }
        setMessageLoading(false);
    };

    const onSelectThread = async (thread) => {
        setSelectedThread(thread);
        setTypingText("");
        setMessages([]);
        await loadMessages(thread.id);
    };

    const emitTyping = (isTyping) => {
        const socket = socketRef.current;
        const threadID = selectedThreadRef.current?.id;
        if (!socket || !threadID) {
            return;
        }

        socket.emit("chat:typing", {
            thread_id: threadID,
            is_typing: !!isTyping,
        });
    };

    const onContentChange = (value = "") => {
        setContent(value);

        const hasValue = String(value || "").trim().length > 0;
        emitTyping(hasValue);

        if (typingEmitTimeoutRef.current) {
            clearTimeout(typingEmitTimeoutRef.current);
        }

        if (hasValue) {
            typingEmitTimeoutRef.current = setTimeout(() => {
                emitTyping(false);
            }, 1200);
        }
    };

    const loadAttachmentPickerData = async ({ type = attachmentPickerType, info = attachmentPickerKeyword } = {}) => {
        setAttachmentPickerLoading(true);
        try {
            if (type === "product") {
                const products = await productService.list({ info: "", page: 1, page_size: 100, status: "show" });
                setAttachmentPickerData(Array.isArray(products) ? products : []);
            } else if (type === "cart") {
                const currentThread = selectedThreadRef.current || selectedThread;
                if (!currentThread?.id) {
                    setAttachmentPickerData([]);
                    setAttachmentPickerLoading(false);
                    return;
                }

                const cart = await service.getCurrentCartByThread({ thread_id: currentThread.id });
                setAttachmentPickerData(cart?.id ? [cart] : []);
            } else if (type === "invoice") {
                const currentThread = selectedThreadRef.current || selectedThread;
                if (!currentThread?.id) {
                    setAttachmentPickerData([]);
                    setAttachmentPickerLoading(false);
                    return;
                }

                const keywordInfo = String(info || "").trim();
                const fallbackInfo = String(currentThread?.customer_phone || currentThread?.customer_name || "").trim();
                const searchInfo = keywordInfo || fallbackInfo;
                const orders = await orderService.list({ info: searchInfo, page: 1, page_size: 30 });
                const list = Array.isArray(orders) ? orders : [];
                const customerOrders = list.filter((order) => isSameCustomerOrder(order, currentThread));
                setAttachmentPickerData(customerOrders);
            } else {
                const orders = await orderService.list({ info: String(info || "").trim(), page: 1, page_size: 30 });
                setAttachmentPickerData(Array.isArray(orders) ? orders : []);
            }
        } catch (error) {
            setAttachmentPickerData([]);
            message.error(error?.message || "Không tải được danh sách");
        }
        setAttachmentPickerLoading(false);
    };

    const openAttachmentPicker = async (type = "product") => {
        setAttachmentPickerType(type);
        setAttachmentPickerKeyword("");
        setAttachmentPickerOpen(true);
        await loadAttachmentPickerData({ type, info: "" });
    };

    const closeAttachmentPicker = () => {
        setAttachmentPickerOpen(false);
    };

    const getProductImageURL = (product = {}) => {
        return (
            resolveFileURL(product?.image_url) ||
            resolveFileURL(product?.thumbnail) ||
            resolveFileURL(product?.url) ||
            resolveFileURL(product?.image) ||
            resolveFileURL(product?.images?.[0]) ||
            ""
        );
    };

    const onPickAttachment = (item = {}) => {
        if (attachmentPickerType === "product") {
            const productID = String(item?.id || item?.product_id || "").trim();
            if (!productID) {
                message.error("Không tìm thấy mã sản phẩm");
                return;
            }

            const title = String(item?.title || item?.name || "Sản phẩm").trim();
            const imageURL = getProductImageURL(item);
            const safeImageURL = /^https?:\/\//i.test(String(imageURL || "").trim()) ? imageURL : "";
            const fallbackVariantPrice = firstPositiveNumber(
                item?.variants?.[0]?.min_price,
                item?.variants?.[0]?.original_price,
                item?.variants?.[0]?.price,
            );
            const resolvedPrice = firstPositiveNumber(
                item?.price,
                item?.min_price,
                item?.original_price,
                item?.calculated_price?.calculated_amount,
                fallbackVariantPrice,
            );

            setAttachments((prev) => ([
                ...prev,
                {
                    type: "product",
                    title,
                    url: safeImageURL,
                    meta: {
                        product_id: productID,
                        product_name: title,
                        price: resolvedPrice,
                        min_price: firstPositiveNumber(item?.min_price, fallbackVariantPrice),
                        original_price: firstPositiveNumber(item?.original_price, item?.price, fallbackVariantPrice),
                    },
                },
            ]));
            setAttachmentPickerOpen(false);
            return;
        }

        if (attachmentPickerType === "cart") {
            const cartID = String(item?.id || "").trim();
            const customerID = String(item?.customer_id || selectedThread?.customer_id || "").trim();
            if (!cartID || !customerID) {
                message.error("Không tìm thấy giỏ hàng hiện tại của khách");
                return;
            }

            setAttachments((prev) => ([
                ...prev,
                {
                    type: "cart",
                    title: "Giỏ hàng hiện tại",
                    meta: {
                        cart_id: cartID,
                        customer_id: customerID,
                        customer_name: item?.customer_name || selectedThread?.customer_name || "",
                        total_item: Number(item?.total_item || 0) || 0,
                        total_amount: Number(item?.total_amount || 0) || 0,
                        route: "/cart",
                    },
                },
            ]));
            setAttachmentPickerOpen(false);
            return;
        }

        if (!item?.id) {
            return;
        }

        const isInvoice = attachmentPickerType === "invoice";
        const type = isInvoice ? "invoice" : "order";
        const orderID = String(item.id || "");
        const orderTitle = isInvoice ? `Hóa đơn #${orderID}` : `Đơn hàng #${orderID}`;

        setAttachments((prev) => ([
            ...prev,
            {
                type,
                title: orderTitle,
                meta: {
                    order_id: orderID,
                    customer_name: item?.customer_name || "",
                    total_item: Number(item?.total_item || 0) || 0,
                    total_amount: item?.total_amount || 0,
                    shipping_address: resolveShippingAddress(item),
                },
            },
        ]));
        setAttachmentPickerOpen(false);
    };

    const onRemoveAttachment = (index) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    const chatImagePickerValue = useMemo(() => {
        return (attachments || [])
            .filter((item) => item?.type === "image")
            .map((item) => item?.meta || { url: item?.url, name: item?.title });
    }, [attachments]);

    const onChangeChatImagePicker = (files = []) => {
        const nonImageAttachments = (attachments || []).filter((item) => item?.type !== "image");
        const imageAttachments = mapFilesToImageAttachments(files);
        setAttachments([...nonImageAttachments, ...imageAttachments]);
    };

    const onSend = async () => {
        if (!selectedThread?.id || sending) {
            return;
        }

        const text = String(content || "").trim();
        if (!text && attachments.length === 0) {
            return;
        }

        setSending(true);
        try {
            const data = await service.sendMessage({
                thread_id: selectedThread.id,
                content: text,
                attachments,
            });
            if (data?.message) {
                setMessages((prev) => {
                    if (prev.some((item) => item.id === data.message.id)) {
                        return prev;
                    }
                    return [...prev, data.message];
                });
            }

            if (data?.thread) {
                setThreads((prev) => sortThreads(prev.map((item) => (item.id === data.thread.id ? data.thread : item))));
                setSelectedThread(data.thread);
            }
            setContent("");
            setAttachments([]);
            setTypingText("");
            emitTyping(false);
        } catch (error) {
            message.error(error?.message || "Không gửi được tin nhắn");
        }
        setSending(false);
    };

    const attachmentPickerOptions = useMemo(() => {
        if (attachmentPickerType === "product") {
            return (attachmentPickerData || [])
                .filter((item) => matchesProductKeyword(item, attachmentPickerKeyword))
                .map((item) => {
                const title = String(item?.title || item?.name || "Sản phẩm").trim();
                const image = getProductImageURL(item);
                return {
                    id: item?.id,
                    title,
                    subtitle: item?.price ? formatCurrency(item.price) : "",
                    image,
                    raw: item,
                };
            });
        }

        if (attachmentPickerType === "cart") {
            return (attachmentPickerData || []).map((item) => {
                const totalAmount = Number(item?.total_amount || 0) || 0;
                const totalItem = Number(item?.total_item || 0) || 0;
                return {
                    id: String(item?.id || "").trim(),
                    title: item?.customer_name ? `Giỏ hàng - ${item.customer_name}` : "Giỏ hàng hiện tại",
                    subtitle: totalAmount ? formatCurrency(totalAmount) : "0đ",
                    createdAt: formatOrderDate(item?.update_on || item?.create_on || item?.createdAt),
                    totalAmount,
                    totalAmountText: totalAmount ? formatCurrency(totalAmount) : "0đ",
                    totalItem,
                    shippingAddress: "",
                    image: "",
                    raw: item,
                };
            });
        }

        return (attachmentPickerData || []).map((item) => {
            const id = String(item?.id || "").trim();
            const customerName = String(item?.customer_name || "").trim();
            const totalAmount = Number(item?.total_amount || 0) || 0;
            const totalItem = Number(item?.total_item || 0) || 0;
            const shippingAddress = resolveShippingAddress(item) || "-";
            return {
                id,
                title: customerName ? `#${id} - ${customerName}` : `#${id}`,
                subtitle: totalAmount ? formatCurrency(totalAmount) : "",
                createdAt: formatOrderDate(item?.create_on || item?.created_on || item?.createdAt),
                totalAmount,
                totalAmountText: totalAmount ? formatCurrency(totalAmount) : "-",
                totalItem,
                shippingAddress,
                image: "",
                raw: item,
            };
        });
    }, [attachmentPickerData, attachmentPickerKeyword, attachmentPickerType]);

    const threadOptions = useMemo(() => threads || [], [threads]);

    return {
        loading,
        messageLoading,
        sending,
        threadOptions,
        selectedThread,
        messages,
        linkPreviews,
        content,
        attachments,
        typingText,
        keyword,
        setKeyword,
        setContent: onContentChange,
        onSearch: () => loadThreads(keyword),
        onReload: () => loadThreads(keyword),
        onSelectThread,
        onSend,
        chatImagePickerValue,
        onChangeChatImagePicker,
        onRemoveAttachment,
        attachmentPickerOpen,
        attachmentPickerType,
        attachmentPickerKeyword,
        attachmentPickerLoading,
        attachmentPickerOptions,
        setAttachmentPickerKeyword,
        openAttachmentPicker,
        closeAttachmentPicker,
        loadAttachmentPickerData,
        onPickAttachment,
        soundEnabled,
        toggleSoundEnabled: () => setSoundEnabled((prev) => !prev),
    };
};

export default useChat;
